import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/zengdai/Workspace/vimwiki/Projects/Blogger/blogv3/src/components/posts-layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`Windows is (slow, power hog, rebooting, ) except gaming. Now let's fix it.`}</p>
    <h1>{`Why`}</h1>
    <p>{`The last time I used Windows as my main laptop, I was left with the strong impression that it was mainly for games. Windows lacks a robust development environment, such as a shell, user administration, etc.. I switched to Linux but struggled for years to find a suitable Linux laptop because of poor Linux hardware driver (therefore low quality trackpad, fan, battery, sleep mode; they seems to only care about desktop). Eventually, I discovered that Macbook was a better alternative if I want to `}{`*`}{`nix flavor, albeit not the most optimal solution. However, with the introduction of WSL(2) on Windows and reduced fan noise on ARM, I now feel that Windows is becoming my best Linux machine.`}</p>
    <p>{`So, this post discusses how to make Windows great again, specifically by transforming it into a capable Linux laptop.`}</p>
    <h1>{`Software (GUI)`}</h1>
    <p>{`"Hey, you don't have apt-get or brew (sucks)!" Well, Scoop.sh is my choice of package manager. I actually only used it for the GUI. Remember, what really matters is the Linux terminal environment. However, we need GUI tools like a browser, media player, GIMP, etc. Scoop can be used for this purpose; it's like a replacement for `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`brew install --cask`}</code>{`.`}</p>
    <p>{`Since most of my work is done in the Linux Terminal, Scoop also serves as my bootstrap tool for Windows. Here's the `}<a parentName="p" {...{
        "href": "https://github.com/daizeng1984/dotfiles/blob/master/windows/installScoop.ps1"
      }}>{`script`}</a>{` I used to install everything on Windows. What's nice is that, with the help of AutoHotkey, this setup also provides me with key/mouse mapping similar to that of Macbook. This way, when switching between Mac and Windows, I experience less muscle memory strain.`}</p>
    <h1>{`Auto Rebooting`}</h1>
    <p>{`This is the most notorious and infamous `}<strong parentName="p">{`"bug"`}</strong>{` that Windows has. When you are giving a presentation or have important work unsaved, Windows will do its best to interrupt your work if you don't stop it. Now we have a solution that allows you to take control. Open your command prompt with administrative rights and paste:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "bash"
    }}><pre parentName="div" {...{
        "className": "language-bash"
      }}><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`FOR %F IN `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"%SystemRoot%\\servicing\\Packages\\Microsoft-Windows-GroupPolicy-ClientTools-Package~*.mum"`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` DO `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`DISM /Online /NoRestart /Add-Package:`}<span parentName="code" {...{
            "className": "token string"
          }}>{`"%F"`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` 
FOR %F IN `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"%SystemRoot%\\servicing\\Packages\\Microsoft-Windows-GroupPolicy-ClientExtensions-Package~*.mum"`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` DO `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`DISM /Online /NoRestart /Add-Package:`}<span parentName="code" {...{
            "className": "token string"
          }}>{`"%F"`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span></code></pre></div>
    <p>{`then go to Start Menu or `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`cmd`}</code>{` to run: `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`gpedit.msc`}</code>{` to open the Group Policy tool.`}</p>
    <p>{`Go to Computer Configuration > Administrative Templates > Windows Components > Windows Update > Configure Automatic Updates > Legacy Policies and find option "No autore-start with logged on users for scheduled automatic updates installations"`}</p>
    <p>{`Note: This policy applies only when Automatic Updates is configured to perform scheduled installations of updates. If the "Configure Automatic Updates" policy is disabled, this policy has no effect.`}</p>
    <p>{`So go to Administrative Templates > Windows Components > Windows Update > Manage end user experience > Configure Automatic Updates make sure this option is either not configured or `}<strong parentName="p">{`NOT`}</strong>{` Disabled`}</p>
    <h1>{`Hardware`}</h1>
    <p>{`Apple has become significantly cooler since the release of the M series. Fortunately, this year, things are set to change. With the introduction of the Snapdragon X Plus, we can now expect much longer battery life for Windows Laptop. I'm still testing it, and so far, so good. There are some detailed comparisons, but it seems to me that Snapdragon will win my preference due to its multicore performance, even though the M series appears to focus more on single-core performance. I can't definitively say which one is better (it's a trade-off), but I prefer multicore performance since the modern software I use in the terminal, like rg and fd, is mostly designed that way. It's blazing fast with such multicore capabilities.`}</p>
    <p>{`But just like on the day of the M1 Mac release, not all apps are compatible. You will definitely encounter some compatibility issues. I would not recommend using this laptop as a Windows gaming machine, not just for performance reasons, but also because there are reported incompatibilities due to poorly designed game anti-cheat executables. You can check app compatibility `}<a parentName="p" {...{
        "href": "https://www.worksonwoa.com/"
      }}>{`here`}</a>{`. `}</p>
    <p>{`I strongly recommend the Surface Laptop 7 XPlus 13" (the one I'm using). You get a nice high refresh rate display, a premium trackpad (equivalent to a MacBook!), and a much nicer keyboard.`}</p>
    <h1>{`WSL`}</h1>
    <p>{`WSL is one of the main reasons why Windows has become my preferred Linux OS, surpassing MacBook's Unix. On the MacBook, many terminal commands are not native arm64 versions, at least the last time I checked. However, WSL is aarch64 and has full native support! The reason for this is that ARM Linux has been around for a while. So far, I haven't encountered any compatibility issues. On my other gaming laptop with Windows and WSL, I can also use nvidia-smi and run GPU/LLM Python projects smoothly. That's truly incredible for me.`}</p>
    <h2>{`Fix My Gatsby`}</h2>
    <p>{`But I have a small problem with an npm package because it is so old that it doesn't have an aarch64 build. In my case, aarch64 needs to build many components on device (many packages are missing) and has broken my old Gatsby setup. I need to found the error log and installed the necessary packages.`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "text"
    }}><pre parentName="div" {...{
        "className": "language-text"
      }}><code parentName="pre" {...{
          "className": "language-text"
        }}>{`apt install build-essential libpng-dev autoconf libtool pkg-config`}</code></pre></div>
    <p>{`I also need to pin the Python version to < 3.10 because these packages are very old. I hesitate to upgrade it because everything breaks.`}</p>
    <h1>{`Misc Fixes`}</h1>
    <p>{`If we like to push a bit further (more arm flavor and less bloaty and junk) to save more battery and perfectionism, we could do these:`}</p>
    <h2>{`Clean Up MS Bloatware`}</h2>
    <p>{`Watch out when using debloater `}<a parentName="p" {...{
        "href": "https://github.com/LeDragoX/Win-Debloat-Tools"
      }}>{`Win-Debloat-Tools`}</a>{`. They are nice but for arm they might brick the machine. So here, we just pick one of the scripts to `}<a parentName="p" {...{
        "href": "https://github.com/daizeng1984/dotfiles/blob/master/windows/disableServices.ps1"
      }}>{`disable services`}</a>{` which disable or manualize most services e.g. EdgeUpdate.`}</p>
    <p>{`I also `}<a parentName="p" {...{
        "href": "https://github.com/daizeng1984/dotfiles/blob/master/windows/Delete_officeclicktorun.reg"
      }}>{`removed the ClickToRun services`}</a>{` because I don't/never use Office (use G Suite). ⚠ Don't do it if you don't know what you are doing.`}</p>
    <p>{`I also did a bit manual touch like:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "text"
    }}><pre parentName="div" {...{
        "className": "language-text"
      }}><code parentName="pre" {...{
          "className": "language-text"
        }}>{`Go to Programs & Features:

Uninstall MS Office other region (why are they installed?)
Uninstall Remote Desktop Connection (feeling insecure)

Go to Edge Settings make it less evil (when you don't use it):
Disable "Startup boost"
Disable "Continue running background extensions and apps when Microsoft Edge is closed"

Go to system > system components, to make them Never run in background:
Phone Link, Game Bar`}</code></pre></div>
    <p>{`I really hope these OS maker would provide a convenient way to customize what you need in OS setup. Too many trackers or services we don't need.`}</p>
    <h2>{`Alacritty Arm64`}</h2>
    <p>{`It's unfornately they are not releasing it for arm64, but you can build by yourself.
First install cli build tools for win: `}<a parentName="p" {...{
        "href": "https://visualstudio.microsoft.com/visual-cpp-build-tools/"
      }}>{`https://visualstudio.microsoft.com/visual-cpp-build-tools/`}</a>{` make sure to check the arm64 components e.g. C++ VS 2017 for ARM64 (I checked all latest ARM64 version)
Then install rust and llvm, I did in git bash or mingw:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "bash"
    }}><pre parentName="div" {...{
        "className": "language-bash"
      }}><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`scoop `}<span parentName="code" {...{
            "className": "token function"
          }}>{`install`}</span>{` main/rustup
scoop `}<span parentName="code" {...{
            "className": "token function"
          }}>{`install`}</span>{` main/llvm-arm64`}</code></pre></div>
    <p>{`run `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`cargo --version`}</code>{` and `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`clang --version`}</code>{` to check if our install succeeds. go clone alacritty repo and:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "bash"
    }}><pre parentName="div" {...{
        "className": "language-bash"
      }}><code parentName="pre" {...{
          "className": "language-bash"
        }}><span parentName="code" {...{
            "className": "token function"
          }}>{`cargo`}</span>{` build `}<span parentName="code" {...{
            "className": "token parameter variable"
          }}>{`--release`}</span></code></pre></div>
    <p>{`Result alacritty.exe should be in target folder.`}</p>
    <h2>{`MP3 Player`}</h2>
    <p>{`Foobar2000 (I used to have it around 2000) have ARM64, just scoop install it.`}</p>
    <h2>{`MP4 Player`}</h2>
    <p>{`VLC nightly shipped with arm64 is still not working! So I choose to use smplayer. For smplayer arm version, go github and download here: `}<a parentName="p" {...{
        "href": "https://github.com/minnyres/smplayer-windows-arm64/releases/tag/v24.5.0/smplayer-portable-24.5.0-arm64.7z"
      }}>{`smplayer-portable-24.5.0-arm64.7z`}</a></p>
    <h2>{`WSL Clip.exe Language Support`}</h2>
    <p>{`When I copy Chinese or other utf character from wsl (which seems to use clip.exe), it convert my chinese character to something unrecognizable. The treatment is just `}<a parentName="p" {...{
        "href": "https://github.com/microsoft/WSL/issues/4852#issuecomment-2112370926"
      }}>{`enabling the settings`}</a>{`: time and language -> administrative language settings -> administrative -> change system locale -> check `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`Beta: Use Unicode UTF-8 for global language support`}</code>{` `}</p>
    <h1>{`Overall`}</h1>
    <p>{`If you work on Linux and spend most of your time in the terminal, I recommend using Windows with WSL. I know I just bought this during Black Friday, so it's still in the honeymoon phase. I'll continue using this favorite laptop and will keep posting if I find anything in the future.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      